import React, { FormEventHandler, useCallback, useMemo, useState } from 'react';

import groupBy from 'lodash/groupBy';

import { Alert, Box, Button, DialogActions, DialogContent, Stack, TextField, Typography } from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import {
  GroupPaymentForOrder,
  REFUNDABLE_SOURCE_TYPE_UNPAID,
  RefundableSource,
  RefundableSourcesResult,
} from '~/services/PaymentsService';

import currencyFormatter from '~/utils/currencyFormatter';
import { sum } from '~/utils/mathUtils';

import { RefundSourceAllocation } from '../../types';
import { ALLOCATABLE_SOURCE_TYPES } from '../Utils/RefundUtils';

import {
  CASH_SOURCE_TYPES,
  associatedCashRemainingForPayer,
  instrumentOwnerKey,
  maxCashAllocationForSource,
} from './associatedCashRemaining';

const ALLOCATION_TOLERANCE = 0.01;
const UNPAID_SHARE_EXPLANATION =
  'This customer has not paid anything. They will not be refunded any money. This refund will just reduce the balance.';

interface Props {
  totalToRefund: number;
  currencyCode: string;
  refundableSources?: RefundableSourcesResult;
  groupPayment?: GroupPaymentForOrder | null;
  initialAllocations?: Array<RefundSourceAllocation>;
  isLoading: boolean;
  decrementStep: () => void;
  onSubmit: (allocations: Array<RefundSourceAllocation>) => void;
}

function sourceKey(source: { type: string; paymentKey: string }): string {
  return `${source.type}::${source.paymentKey}`;
}

function describePayer(source: RefundableSource): string {
  return source.email ?? 'Unknown payer';
}

function allocatedAmount(
  source: { type: string; paymentKey: string },
  allocationInputs: Record<string, string>,
): number {
  const parsed = parseFloat(allocationInputs[sourceKey(source)] || '0');
  return Number.isFinite(parsed) ? parsed : 0;
}

function describeSource(source: RefundableSource): string {
  if (source.type === REFUNDABLE_SOURCE_TYPE_UNPAID) {
    return 'Unpaid share';
  }
  return source.display.last4 ? `${source.display.label} •••• ${source.display.last4}` : source.display.label;
}

// Rebuild form field keys (type::paymentKey) from saved RefundSourceAllocation rows when remounting after Back from Confirm.
function initialAllocationInputs(initialAllocations?: Array<RefundSourceAllocation>): Record<string, string> {
  if (!initialAllocations?.length) {
    return {};
  }
  return Object.fromEntries(
    initialAllocations.map((allocation) => [
      sourceKey({ type: allocation.type, paymentKey: allocation.payment_key }),
      String(allocation.amount),
    ]),
  );
}

export default function GroupPaymentRefundAllocation({
  totalToRefund,
  currencyCode,
  refundableSources,
  groupPayment,
  initialAllocations,
  isLoading,
  decrementStep,
  onSubmit,
}: Props) {
  const [allocationInputs, setAllocationInputs] = useState<Record<string, string>>(() =>
    initialAllocationInputs(initialAllocations),
  );

  const allocatableSources = useMemo(
    () => (refundableSources?.sources ?? []).filter((source) => ALLOCATABLE_SOURCE_TYPES.includes(source.type)),
    [refundableSources],
  );

  const sourcesByPayer = useMemo(
    () => groupBy(allocatableSources, (source) => describePayer(source)),
    [allocatableSources],
  );

  const cashSources = useMemo(
    () => allocatableSources.filter((source) => CASH_SOURCE_TYPES.includes(source.type)),
    [allocatableSources],
  );

  const totalAllocated = useMemo(
    () => sum(allocatableSources.map((source) => allocatedAmount(source, allocationInputs))),
    [allocatableSources, allocationInputs],
  );

  const remaining = Number((totalToRefund - totalAllocated).toFixed(2));
  const isFullyAllocated = Math.abs(remaining) < ALLOCATION_TOLERANCE;

  const handleAllocationChange = useCallback(
    (source: RefundableSource, rawValue: string) => {
      setAllocationInputs((previous) => {
        const parsed = parseFloat(rawValue);
        if (!Number.isFinite(parsed)) {
          return {
            ...previous,
            [sourceKey(source)]: '',
          };
        }

        const ownerKey = instrumentOwnerKey(source);
        const otherCashAllocated = sum(
          cashSources
            .filter(
              (cashSource) =>
                sourceKey(cashSource) !== sourceKey(source) && instrumentOwnerKey(cashSource) === ownerKey,
            )
            .map((cashSource) => allocatedAmount(cashSource, previous)),
        );
        const max = maxCashAllocationForSource({
          source,
          otherCashAllocated,
          groupPayment,
        });
        const clamped = Math.min(Math.max(0, parsed), max);

        return {
          ...previous,
          [sourceKey(source)]: clamped === parsed ? rawValue : clamped.toFixed(2),
        };
      });
    },
    [cashSources, groupPayment],
  );

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      if (!isFullyAllocated) {
        return;
      }
      const allocations: Array<RefundSourceAllocation> = allocatableSources.flatMap((source) => {
        const parsed = parseFloat(allocationInputs[sourceKey(source)] || '0');
        if (!Number.isFinite(parsed) || parsed <= 0) {
          return [];
        }
        return [{ type: source.type, payment_key: source.paymentKey, amount: parsed.toFixed(2) }];
      });
      onSubmit(allocations);
    },
    [allocatableSources, allocationInputs, isFullyAllocated, onSubmit],
  );

  if (isLoading) {
    return (
      <DialogContent>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Spinner />
        </Box>
      </DialogContent>
    );
  }

  const orderLevel = refundableSources?.orderLevel;
  const orderLevelParts = [
    orderLevel?.promo.remainingAmount
      ? `promo ${currencyFormatter(currencyCode, orderLevel.promo.remainingAmount, 2)}`
      : null,
    orderLevel?.voucher.remainingAmount
      ? `voucher ${currencyFormatter(currencyCode, orderLevel.voucher.remainingAmount, 2)}`
      : null,
    orderLevel?.lePointsBenefits.remainingAmount
      ? `LE-points benefits ${currencyFormatter(currencyCode, orderLevel.lePointsBenefits.remainingAmount, 2)}`
      : null,
  ].filter(Boolean);

  return (
    <>
      <DialogContent dividers>
        <form onSubmit={handleSubmit} id="group-payment-refund-allocation-form">
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              This is a Group Payment order with multiple payers. Allocate the refund of{' '}
              <b>{currencyFormatter(currencyCode, totalToRefund, 2)}</b> across the payment sources below. No payer can
              be refunded more than they paid.
            </Typography>

            {Object.entries(sourcesByPayer).map(([payer, sources]) => {
              const payerCashSources = sources.filter((source) => CASH_SOURCE_TYPES.includes(source.type));
              const payerCustomerId = payerCashSources.find((source) => source.customerId)?.customerId;
              const showCashAllocated = payerCashSources.length > 0 && !!groupPayment && !!payerCustomerId;
              const cashAllocated = sum(payerCashSources.map((source) => allocatedAmount(source, allocationInputs)));
              const cashRemaining =
                groupPayment && payerCustomerId ? associatedCashRemainingForPayer(payerCustomerId, groupPayment) : 0;

              return (
                <Stack key={payer} spacing={1}>
                  <Typography fontWeight="bold">{payer}</Typography>
                  {sources.map((source) => (
                    <Stack key={sourceKey(source)} spacing={0.5}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" color="text.secondary" flex={1}>
                          {describeSource(source)} — refundable{' '}
                          {currencyFormatter(currencyCode, source.refundableAmount, 2)}
                        </Typography>
                        <TextField
                          value={allocationInputs[sourceKey(source)] ?? ''}
                          onChange={(e) => handleAllocationChange(source, e.target.value)}
                          type="number"
                          hiddenLabel
                          size="small"
                          inputProps={{
                            min: 0,
                            max: source.refundableAmount,
                            step: '.01',
                            inputMode: 'decimal',
                            'aria-label': `Refund allocation for ${describePayer(source)}`,
                          }}
                          sx={{ width: 160 }}
                        />
                      </Stack>
                      {source.type === REFUNDABLE_SOURCE_TYPE_UNPAID && (
                        <Typography variant="caption" color="text.secondary">
                          {UNPAID_SHARE_EXPLANATION}
                        </Typography>
                      )}
                    </Stack>
                  ))}
                  {showCashAllocated && (
                    <Typography variant="body2" color="text.secondary">
                      Cash allocated {currencyFormatter(currencyCode, cashAllocated, 2)} of{' '}
                      {currencyFormatter(currencyCode, cashRemaining, 2)}
                    </Typography>
                  )}
                </Stack>
              );
            })}

            <Alert severity={isFullyAllocated ? 'success' : 'info'}>
              Allocated {currencyFormatter(currencyCode, totalAllocated, 2)} of{' '}
              {currencyFormatter(currencyCode, totalToRefund, 2)}
              {!isFullyAllocated && <> — {currencyFormatter(currencyCode, remaining, 2)} remaining to allocate.</>}
            </Alert>

            {orderLevelParts.length > 0 && (
              <Alert severity="info">
                Order-level payments ({orderLevelParts.join(', ')}) cannot be allocated here. This order uses a payment
                schedule, so promo codes and vouchers are not refunded automatically; LE-points benefits are refunded in
                full when the item is cancelled.
              </Alert>
            )}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={decrementStep}>Back</Button>
        <Button
          type="submit"
          form="group-payment-refund-allocation-form"
          variant="contained"
          disabled={!isFullyAllocated}
        >
          Continue
        </Button>
      </DialogActions>
    </>
  );
}
